import * as React from "react";

import { useModal } from "@editor/hooks/useModal";

import { useLocation, useParams } from "react-router-dom";

interface ShopCreationFlowState {
  projectId: string;
  workspaceId: string;
  step: number;
  shopInfo: {
    name: string;
    subdomain: string;
    productDescription: string;
    annualRevenue: string;
  };
  timestamp: number;
}

/**
 * Hook that detects when a user returns from Shopify integration during shop creation
 * and redirects them back to the appropriate step in the shop creation modal.
 */
export function useShopCreationFlowRedirect() {
  const { openModal } = useModal();
  const location = useLocation();
  const { projectId: projectIdFromUrl } = useParams<{ projectId: string }>();

  React.useEffect(() => {
    const isEditorPage = location.pathname.startsWith("/editor/");

    if (!isEditorPage) {
      return;
    }

    const shopCreationFlowData = localStorage.getItem("shopCreationFlow");

    if (!shopCreationFlowData) {
      return;
    }

    try {
      const flowState: ShopCreationFlowState = JSON.parse(shopCreationFlowData);

      const isRecent = Date.now() - flowState.timestamp < 10 * 60 * 1000;

      if (!isRecent) {
        localStorage.removeItem("shopCreationFlow");
        return;
      }

      if (projectIdFromUrl !== flowState.projectId) {
        return;
      }

      localStorage.removeItem("shopCreationFlow");

      localStorage.setItem("onboarding-setup-selected-option", '"import"');

      setTimeout(() => {
        openModal({
          type: "createShopFlowModal",
          props: {
            workspaceId: flowState.workspaceId,
            initialStep: flowState.step,
            shopInfo: flowState.shopInfo,
            projectId: flowState.projectId,
          },
        });
      }, 100);
    } catch (error) {
      console.error("Error parsing shop creation flow data:", error);
      localStorage.removeItem("shopCreationFlow");
    }
  }, [location.pathname, openModal, projectIdFromUrl]);
}
